import React, { useState } from 'react';
import { Youtube, Play, X } from 'lucide-react';
import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'card',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultPadding: 'normal',
};

/**
 * YouTubeGalleryRenderer - Render component for youtube-gallery block
 * Displays videos from the synced youtubeVideos array
 * Note: YouTube red play button is kept as brand color
 */
const YouTubeGalleryRenderer = ({ block, youtubeVideos = [], theme = {} }) => {
  const { content } = block;
  const {
    heading,
    showAll = true,
    selectedVideoIds = [],
    maxVideos = 0,
    showTitles = true,
    showDescriptions = false,
  } = content;

  const [activeVideo, setActiveVideo] = useState(null);

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  // Filter videos based on settings
  let displayVideos = showAll
    ? youtubeVideos
    : youtubeVideos.filter((v) => selectedVideoIds.includes(v.id));

  // Apply max limit if set
  if (maxVideos > 0 && showAll) {
    displayVideos = displayVideos.slice(0, maxVideos);
  }

  // Get YouTube thumbnail
  const getYouTubeThumbnail = (videoId, quality = 'mqdefault') => {
    return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
  };

  // No videos
  if (displayVideos.length === 0) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.cardBackground }}
      >
        <div className="max-w-6xl mx-auto text-center">
          <Youtube size={48} className="mx-auto mb-4" style={{ color: styles.mutedColor }} />
          <p style={{ color: styles.mutedColor }}>No videos to display</p>
        </div>
      </div>
    );
  }

  // Video modal
  const VideoModal = () => {
    if (!activeVideo) return null;

    return (
      <div
        className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
        onClick={() => setActiveVideo(null)}
      >
        <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setActiveVideo(null)}
            className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
          >
            <X size={24} />
          </button>
          <div className="aspect-video bg-black rounded-lg overflow-hidden">
            <iframe
              src={`https://www.youtube.com/embed/${activeVideo.videoId}?autoplay=1`}
              title={activeVideo.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          {showTitles && activeVideo.title && (
            <p className="text-white text-lg font-medium mt-4">{activeVideo.title}</p>
          )}
        </div>
      </div>
    );
  };

  // Video card component
  const VideoCard = ({ video }) => (
    <button
      onClick={() => setActiveVideo(video)}
      className="group flex-none w-[280px] sm:w-[320px] rounded-xl overflow-hidden text-left transition-all hover:translate-y-[-2px] snap-start"
      style={{
        backgroundColor: styles.sectionBackground,
        border: `1px solid ${styles.borderColor}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = `${styles.accentColor}80`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = styles.borderColor;
      }}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video">
        <img
          src={getYouTubeThumbnail(video.videoId, 'mqdefault')}
          alt={video.title}
          className="w-full h-full object-cover"
        />
        {/* Play overlay - YouTube red is kept as brand color */}
        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-red-600 group-hover:bg-red-500 transition-colors flex items-center justify-center">
            <Play size={24} className="text-white ml-1" fill="white" />
          </div>
        </div>
      </div>

      {/* Info */}
      {(showTitles || showDescriptions) && (
        <div className="p-4">
          {showTitles && video.title && (
            <h4
              className="text-sm sm:text-base font-medium line-clamp-2"
              style={{ color: styles.headingColor }}
            >
              {video.title}
            </h4>
          )}
          {showDescriptions && video.description && (
            <p
              className="text-xs sm:text-sm mt-1 line-clamp-2"
              style={{ color: styles.mutedColor }}
            >
              {video.description}
            </p>
          )}
        </div>
      )}
    </button>
  );

  return (
    <>
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.cardBackground }}
      >
        <div className="max-w-6xl mx-auto">
          {/* Section Heading */}
          {heading && (
            <h3
              className={`${styles.headingSizeClass} font-bold ${styles.headingAlignmentClass} mb-6 sm:mb-8 md:mb-10`}
              style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
            >
              {heading}
            </h3>
          )}

          {/* Video Carousel - Horizontal Swipe */}
          <div
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {displayVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>

          {/* Hide scrollbar CSS */}
          <style>{`
            .overflow-x-auto::-webkit-scrollbar {
              display: none;
            }
          `}</style>
        </div>
      </div>

      {/* Video Modal */}
      <VideoModal />
    </>
  );
};

export default YouTubeGalleryRenderer;
